import React, { FC, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import useService from '../../../hooks/service.hook';
import InvitationsService from '../../../services/invitations.service';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import COESkeletonComponent from '../../general/coe-skeleton/coe-skeleton.component';
import DraftsIcon from '@mui/icons-material/Drafts';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import './notifications.link.css';
import NotificationsService from '../../../services/notifications.service';
import MessageIcon from '@mui/icons-material/Message';
import { useNavigate } from 'react-router-dom';
import GuardianService from '../../../services/guardian.service';
import EquipmentSharingService from '../../../services/equipment-sharing.service';
import TeamsService from '../../../services/teams.service';
import BuildIcon from '@mui/icons-material/Build';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { palette } from '../../../theme';

/** MUI reserves little padding for secondaryAction; two buttons need more, plus flex text must shrink and wrap. */
const notificationListItemSxTwoButtons = {
  alignItems: 'flex-start' as const,
  pr: '180px',
  '& .MuiListItemText-root': { minWidth: 0 },
};

const notificationListItemSxOneButton = {
  alignItems: 'flex-start' as const,
  pr: '100px',
  '& .MuiListItemText-root': { minWidth: 0 },
};

const notificationPrimaryTypographyProps = {
  sx: {
    overflowWrap: 'break-word' as const,
    wordBreak: 'break-word' as const,
    whiteSpace: 'normal' as const,
  },
};

interface Notification {
  id: number;
  message: string;
  related_id: number;
  team_id: number | null;
  type:
    | 'thread'
    | 'team_invitation'
    | 'team_join_request'
    | 'guardian_invitation'
    | 'equipment_invitation'
    | 'equipment_ownership_transfer';
  read: boolean;
  created_at: string;
}

interface TeamInvitationNotificationProps {
  index: number;
  option: Notification;
  acceptInvitation: any;
  declineInvitation: any;
}

const GuardianInvitationNotification: React.FC<TeamInvitationNotificationProps> = ({
  index,
  option,
  acceptInvitation,
  declineInvitation,
}) => {
  return (
    <ListItem
      key={index}
      sx={notificationListItemSxTwoButtons}
      secondaryAction={
        <>
          <Button size="small" onClick={() => acceptInvitation(option.related_id)}>
            Accept
          </Button>
          <Button size="small" onClick={() => declineInvitation(option.related_id)} color="error">
            Decline
          </Button>
        </>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <SupervisorAccountIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        secondary={format(parseISO(option.created_at), 'MMM do, Y')}
        primaryTypographyProps={notificationPrimaryTypographyProps}
      />
    </ListItem>
  );
};

interface TeamInvitationNotificationProps {
  index: number;
  option: Notification;
  acceptInvitation: any;
  declineInvitation: any;
}

const TeamInvitationNotification: React.FC<TeamInvitationNotificationProps> = ({
  index,
  option,
  acceptInvitation,
  declineInvitation,
}) => {
  return (
    <ListItem
      key={index}
      sx={notificationListItemSxTwoButtons}
      secondaryAction={
        <>
          <Button size="small" onClick={() => acceptInvitation(option.team_id)}>
            Accept
          </Button>
          <Button size="small" onClick={() => declineInvitation(option.team_id)} color="error">
            Decline
          </Button>
        </>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <GroupOutlinedIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        secondary={format(parseISO(option.created_at), 'MMM do, Y')}
        primaryTypographyProps={notificationPrimaryTypographyProps}
      />
    </ListItem>
  );
};

interface TeamJoinRequestNotificationProps {
  index: number;
  option: Notification;
  approveJoinRequest: (teamId: number | null, joinRequestId: number) => void;
  denyJoinRequest: (teamId: number | null, joinRequestId: number) => void;
}

const TeamJoinRequestNotification: React.FC<TeamJoinRequestNotificationProps> = ({
  index,
  option,
  approveJoinRequest,
  denyJoinRequest,
}) => {
  return (
    <ListItem
      key={index}
      sx={notificationListItemSxTwoButtons}
      secondaryAction={
        <>
          <Button
            size="small"
            onClick={() => approveJoinRequest(option.team_id, option.related_id)}
            disabled={option.team_id == null}
          >
            Approve
          </Button>
          <Button
            size="small"
            onClick={() => denyJoinRequest(option.team_id, option.related_id)}
            color="error"
            disabled={option.team_id == null}
          >
            Deny
          </Button>
        </>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <PersonAddIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        secondary={format(parseISO(option.created_at), 'MMM do, Y')}
        primaryTypographyProps={notificationPrimaryTypographyProps}
      />
    </ListItem>
  );
};

interface ThreadNotificationProps {
  index: number;
  option: Notification;
  handleClose: any;
  handleRefresh: any;
}

const ThreadNotification: React.FC<ThreadNotificationProps> = ({ index, option, handleClose, handleRefresh }) => {
  const navigate = useNavigate();

  return (
    <ListItem
      key={index}
      sx={notificationListItemSxOneButton}
      secondaryAction={
        <Button
          size="small"
          onClick={() => {
            handleRefresh();
            handleClose();
            navigate(`/teams/${option.team_id}/messages?message=${option.related_id}`);
          }}
        >
          View
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <MessageIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        secondary={format(parseISO(option.created_at), 'MMM do, Y')}
        primaryTypographyProps={notificationPrimaryTypographyProps}
      />
    </ListItem>
  );
};

interface EquipmentInvitationNotificationProps {
  index: number;
  option: Notification;
  acceptInvitation: any;
  declineInvitation: any;
}

const EquipmentInvitationNotification: React.FC<EquipmentInvitationNotificationProps> = ({
  index,
  option,
  acceptInvitation,
  declineInvitation,
}) => {
  return (
    <ListItem
      key={index}
      sx={notificationListItemSxTwoButtons}
      secondaryAction={
        <>
          <Button size="small" onClick={() => acceptInvitation(option.related_id)}>
            Accept
          </Button>
          <Button size="small" onClick={() => declineInvitation(option.related_id)} color="error">
            Decline
          </Button>
        </>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <BuildIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        secondary={format(parseISO(option.created_at), 'MMM do, Y')}
        primaryTypographyProps={notificationPrimaryTypographyProps}
      />
    </ListItem>
  );
};

interface EquipmentOwnershipTransferNotificationProps {
  index: number;
  option: Notification;
  acceptInvitation: any;
  declineInvitation: any;
}

const EquipmentOwnershipTransferNotification: React.FC<EquipmentOwnershipTransferNotificationProps> = ({
  index,
  option,
  acceptInvitation,
  declineInvitation,
}) => {
  return (
    <ListItem
      key={index}
      sx={notificationListItemSxTwoButtons}
      secondaryAction={
        <>
          <Button size="small" onClick={() => acceptInvitation(option.related_id)}>
            Accept
          </Button>
          <Button size="small" onClick={() => declineInvitation(option.related_id)} color="error">
            Decline
          </Button>
        </>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <SwapHorizIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={option.message}
        primaryTypographyProps={notificationPrimaryTypographyProps}
        secondary={
          <Box sx={{ minWidth: 0, maxWidth: '100%', overflowWrap: 'break-word' }}>
            <Typography variant="caption" display="block">
              {format(parseISO(option.created_at), 'MMM do, Y')}
            </Typography>
            <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem', py: 0.5 }}>
              Accepting this will make you the owner. The current owner will become a manager.
            </Alert>
          </Box>
        }
      />
    </ListItem>
  );
};

interface NotificationsLinkProps {
  sidebarOpen?: boolean;
}

const NotificationsLink: FC<NotificationsLinkProps> = ({ sidebarOpen = true }) => {
  // hooks
  const invitationsService = useService(InvitationsService);
  const notificationsService = useService(NotificationsService);
  const guardianService = useService(GuardianService);
  const equipmentSharingService = useService(EquipmentSharingService);
  const teamsService = useService(TeamsService);
  const { enqueueSnackbar } = useSnackbar();

  // states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const open = Boolean(anchorEl);

  // componentDidMount
  useEffect(() => {
    getNotifications();
  }, []);

  const getNotifications = () => {
    setLoading(true);
    notificationsService
      .getNotifications()
      .then((response) => {
        setNotifications(response.data.data);
        setLastUpdated(format(new Date(), `MMM dd, p`));
        setLoading(false);
      })
      .catch(() => {
        setNotifications([]);
        setLastUpdated(format(new Date(), `MMM dd, p`));
        setLoading(false);
      });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const acceptInvitation = (teamId: string) => {
    invitationsService
      .acceptInvitation(teamId)
      .then(() => {
        enqueueSnackbar('Successfully updated invitation', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch((err) => {
        if (err.response?.status === 409) {
          const message = err.response?.data?.message;
          enqueueSnackbar(
            typeof message === 'string' && message
              ? message
              : 'This team is full. Ask the coach to free a spot or change the plan.',
            {
              autoHideDuration: 5000,
              variant: 'error',
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            }
          );
        } else if (err.response.status === 403) {
          getNotifications();
          enqueueSnackbar(
            'The team you are trying to join has too many athletes. Please reach out to the coach for assistance',
            {
              autoHideDuration: 3000,
              variant: 'error',
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            }
          );
        } else if (err?.response?.status === 422) {
          enqueueSnackbar(
            'You are too young to join this team without a guardian account linked. Please add a guardian before joining this invitation.',
            {
              autoHideDuration: 3000,
              variant: 'error',
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            }
          );
        } else {
          enqueueSnackbar('There was an error while accepting this invitation. Please try again later', {
            autoHideDuration: 3000,
            variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        }
      });
  };

  const declineInvitation = (teamId: string) => {
    invitationsService
      .declineInvitation(teamId)
      .then(() => {
        enqueueSnackbar('Successfully updated invitation', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch(() => {
        enqueueSnackbar('There was an error while accepting this invitation. Please try again later', {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const acceptGuardianInvitation = (guardianInvitationId: string) => {
    guardianService
      .acceptGuardianInvitation(guardianInvitationId)
      .then(() => {
        enqueueSnackbar('Successfully accepted guardian invitation. Your accounts are not linked', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch((error) => {
        if (error?.response?.status === 422) {
          enqueueSnackbar(
            'You are not old enough to be a guardian! Guardians must be at least 18 years old. Please verify your age is correct in settings',
            {
              autoHideDuration: 5000,
              variant: 'error',
              anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
            }
          );
        } else {
          enqueueSnackbar('There was an error while accepting this invitation. Please try again later', {
            autoHideDuration: 3000,
            variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        }
      });
  };

  const declineGuardianInvitation = (guardianInvitationId: string) => {
    guardianService
      .declineGuardianInvitation(guardianInvitationId)
      .then(() => {
        enqueueSnackbar('Successfully declined guardian invitation. Your accounts are not linked', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch(() => {
        enqueueSnackbar('There was an error while declining this invitation. Please try again later', {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const acceptEquipmentInvitation = (invitationId: number) => {
    equipmentSharingService
      .acceptInvitation(invitationId)
      .then(() => {
        enqueueSnackbar('Successfully accepted equipment invitation', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch((err) => {
        let errorMessage = 'There was an error while accepting this invitation. Please try again later';
        if (err?.response?.status === 400) {
          errorMessage = 'This invitation is no longer valid.';
        } else if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to accept this invitation.';
        } else if (err?.response?.status === 404) {
          errorMessage = 'Invitation or equipment not found.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const declineEquipmentInvitation = (invitationId: number) => {
    equipmentSharingService
      .declineInvitation(invitationId)
      .then(() => {
        enqueueSnackbar('Successfully declined equipment invitation', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch((err) => {
        let errorMessage = 'There was an error while declining this invitation. Please try again later';
        if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to decline this invitation.';
        } else if (err?.response?.status === 404) {
          errorMessage = 'Invitation not found.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const decideJoinRequest = (teamId: number | null, joinRequestId: number, decision: 'approve' | 'deny') => {
    if (teamId == null) {
      enqueueSnackbar('Could not update the request.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      return;
    }
    teamsService
      .putTeamJoinRequestDecision(String(teamId), joinRequestId, decision)
      .then((response) => {
        enqueueSnackbar(response.data?.message || (decision === 'approve' ? 'Approved.' : 'Denied.'), {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        getNotifications();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Could not update the request.';
        enqueueSnackbar(typeof msg === 'string' ? msg : 'Could not update the request.', {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const approveJoinRequest = (teamId: number | null, joinRequestId: number) => {
    decideJoinRequest(teamId, joinRequestId, 'approve');
  };

  const denyJoinRequest = (teamId: number | null, joinRequestId: number) => {
    decideJoinRequest(teamId, joinRequestId, 'deny');
  };

  const notificationsContent = () => {
    if (loading) {
      return (
        <div style={{ padding: 10 }}>
          <COESkeletonComponent />
        </div>
      );
    } else if (notifications.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 30,
          }}
        >
          <DraftsIcon style={{ width: 70, height: 70, color: 'grey' }} />
          <Typography color={'gray'}>No notifications</Typography>
        </div>
      );
    } else {
      return (
        <List
          sx={{
            width: '100%',
            maxHeight: 300,
            overflow: 'scroll',
            '& .MuiListItemButton-root:hover': {
              bgcolor: palette.grey[500],
            },
          }}
        >
          {notifications.map((notification: Notification, index) => {
            if (notification.type === 'team_invitation') {
              return (
                <TeamInvitationNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  acceptInvitation={acceptInvitation}
                  declineInvitation={declineInvitation}
                />
              );
            } else if (notification.type === 'guardian_invitation') {
              return (
                <GuardianInvitationNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  acceptInvitation={acceptGuardianInvitation}
                  declineInvitation={declineGuardianInvitation}
                />
              );
            } else if (notification.type === 'equipment_invitation') {
              return (
                <EquipmentInvitationNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  acceptInvitation={acceptEquipmentInvitation}
                  declineInvitation={declineEquipmentInvitation}
                />
              );
            } else if (notification.type === 'equipment_ownership_transfer') {
              return (
                <EquipmentOwnershipTransferNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  acceptInvitation={acceptEquipmentInvitation}
                  declineInvitation={declineEquipmentInvitation}
                />
              );
            } else if (notification.type === 'team_join_request') {
              return (
                <TeamJoinRequestNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  approveJoinRequest={approveJoinRequest}
                  denyJoinRequest={denyJoinRequest}
                />
              );
            } else {
              return (
                <ThreadNotification
                  key={notification.id}
                  index={index}
                  option={notification}
                  handleClose={handleClose}
                  handleRefresh={getNotifications}
                />
              );
            }
          })}
        </List>
      );
    }
  };

  return (
    <>
      <ListItemButton
        sx={{
          height: 40,
          overflow: 'hidden',
          mx: 1,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          '&:hover': { backgroundColor: 'rgba(209,225,255,0.4)' },
          ...(!sidebarOpen && {
            justifyContent: 'center',
            px: 0,
            '& .MuiListItemText-root': { display: 'none' },
            '& .MuiListItemIcon-root': { minWidth: 'unset', mr: 0, justifyContent: 'center' },
          }),
        }}
        onClick={handleClick}
      >
        <ListItemIcon sx={{ minWidth: 32, mr: 0.5 }}>
          <NotificationsOutlined sx={{ fontSize: 20, color: palette.text.primary }} />
        </ListItemIcon>
        <ListItemText
          primary={'Notifications'}
          sx={{ flex: 1, m: 0 }}
          primaryTypographyProps={{
            sx: {
              fontSize: 12,
              fontWeight: 400,
              color: palette.text.primary,
            },
          }}
        />
        {sidebarOpen && notifications.length > 0 && (
          <Box
            component="span"
            sx={{
              minWidth: 18,
              height: 18,
              px: 0.5,
              borderRadius: '50%',
              bgcolor: palette.error.main,
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mr: 0.5,
            }}
          >
            {notifications.length}
          </Box>
        )}
      </ListItemButton>
      <Menu
        id="long-menu"
        className="menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: '100%',
            maxWidth: 550,
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            padding: 5,
            justifyContent: 'space-between',
          }}
        >
          <Typography>Last Updated: {lastUpdated}</Typography>
          <IconButton onClick={getNotifications}>
            <RefreshIcon color="primary" />
          </IconButton>
        </div>
        {notificationsContent()}
      </Menu>
    </>
  );
};

export default NotificationsLink;
